import React from 'react';
import styled from 'styled-components';
import { useFormikContext, type FormikErrors } from 'formik';
import { Box, Stack, Grid } from '@mui/material';
import type { MeritAssessmentsValue, MeritAssessmentsType } from './types';
import { meritTypeOptions } from './options';
import { MeritType } from '../../constants';
import { PAGE_TEXT } from '../../../../lang';
import {
  AssessmentAccordion,
  AssessmentGrid,
  type RowData,
} from '../../../../components';
import { useGetMeritData } from '../../../../graphql';
import {
  TextField,
  Select,
  Loading,
  Alert,
  DisplayFileData,
} from '../../../../../../components';

type MeritAssessmentsContentProps = {
  index: number;
  isViewMode?: boolean;
};

const STEP_TEXT = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_C;
export const MeritAssessmentsContent: React.FunctionComponent<
  MeritAssessmentsContentProps
> = props => {
  const { index: meritAssessIndex, isViewMode = false } = props;
  const { values, handleBlur, setFieldValue, errors, touched } =
    useFormikContext<MeritAssessmentsValue>();

  const meritType =
    values?.meritAssessments?.[meritAssessIndex]?.meritType ?? '';
  const researchLevel =
    values?.meritAssessments?.[meritAssessIndex]?.researchLevel;

  const getScoreFromMeritType = (type?: MeritType | null): number | '' => {
    switch (type) {
      case null:
      case undefined:
        return '';
      case MeritType.NA:
        return 0;
      case MeritType.OTHER:
        return 1;
      case MeritType.PRESTIGIOUS:
        return 3;
      default:
        return 0;
    }
  };

  const {
    merit,
    loading,
    error: meritDataError,
  } = useGetMeritData(
    values?.meritAssessments?.[meritAssessIndex]?.meritReferenceId ?? '',
  );

  const meritAssessmentErrors = errors.meritAssessments as
    | Array<FormikErrors<MeritAssessmentsType>>
    | undefined;

  const handleMeritTypeChange = async (
    event: React.ChangeEvent<{ value: string }>,
  ): Promise<void> => {
    await setFieldValue(
      `meritAssessments.${meritAssessIndex}.meritType`,
      event.target.value as MeritType,
    );
    await setFieldValue(
      `meritAssessments.${meritAssessIndex}.researchLevel`,
      getScoreFromMeritType(event.target.value as MeritType),
    );
  };

  const { supportingDocuments } = merit;

  const InfoDetailsRowData: RowData[] = [
    {
      label: <LabelH2>{STEP_TEXT.MERIT_NAME}</LabelH2>,
      value: merit.meritName,
    },
    {
      label: <LabelH2>{STEP_TEXT.AWARDING_BODY}</LabelH2>,
      value: merit.awardBodyName,
    },
    {
      label: <LabelH2>{STEP_TEXT.AWARD_POSITION}</LabelH2>,
      value: merit.country,
    },
    {
      label: <LabelH2>{STEP_TEXT.AWARD_YEAR}</LabelH2>,
      value: merit.awardYear,
    },
    {
      label: <LabelH2>{STEP_TEXT.SUPPORTTING}</LabelH2>,
      value: <DisplayFileData fileData={supportingDocuments ?? []} />,
    },
  ];

  if (meritDataError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: 'Error Occurs.' + (meritDataError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <ContentBox>
      {loading ? (
        <Loading />
      ) : (
        <Stack>
          <AssessmentAccordion
            defaultExpanded
            className="w-full accordion-content"
            title={`${STEP_TEXT.SUBTITLE} - ${props.index + 1}`}
            InfoDetails={
              <Box padding={2} fontSize={14}>
                <h2 className="italic mb-4">{STEP_TEXT.SPECIFY}</h2>
                <AssessmentGrid rows={InfoDetailsRowData} />
              </Box>
            }
            AssessDetails={
              <Box marginY={2}>
                <Grid className="mt-4" container rowGap={2}>
                  <Grid container size={2}>
                    {STEP_TEXT.MERIT_TYPE.TITLE}
                  </Grid>
                  <Grid container justifyContent="space-between" size={10}>
                    <Select
                      options={meritTypeOptions}
                      name={`meritAssessments.${meritAssessIndex}.meritType`}
                      disabled={isViewMode}
                      value={meritType}
                      InputLabelProps={{ shrink: true }}
                      onChange={handleMeritTypeChange}
                      onBlur={handleBlur}
                      error={
                        touched.meritAssessments?.[meritAssessIndex]
                          ?.meritType &&
                        Boolean(
                          meritAssessmentErrors?.[meritAssessIndex]?.meritType,
                        )
                      }
                      helperText={
                        touched.meritAssessments?.[meritAssessIndex]
                          ?.meritType &&
                        meritAssessmentErrors?.[meritAssessIndex]?.meritType
                      }
                      size="small"
                      sx={{ width: '100%' }}
                    />
                  </Grid>

                  <Grid container size={2}>
                    {STEP_TEXT.RL}
                  </Grid>
                  <Grid container size={10}>
                    <TextField
                      id="researchLevel"
                      name="researchLevel"
                      size="small"
                      inputProps={{
                        style: { textAlign: 'right' },
                      }}
                      value={researchLevel}
                      disabled
                      sx={{
                        width: '25%',
                        background: '#E6E6E6',
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            }
          />
          <hr />
        </Stack>
      )}
    </ContentBox>
  );
};

const ContentBox = styled.div`
  flex-direction: column;
  align-items: center;
  flex: 1;
  hr {
    width: 100%;
    justify-content: flex-start;
    background-image: linear-gradient(to right, #666, #ccc, #fff);
  }
`;

const LabelH2 = styled.h2`
  color: #054783;
`;
